// src/app/modules/adminrh/parametres/ParametrageAttestations/ParametrageAttestations.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { Plus, X, Trash2, Eye, FileText, Bold, Italic, Underline, Code, Pencil, AlignLeft, AlignCenter, AlignRight, AlignJustify, List, ListOrdered, IndentIncrease, IndentDecrease, RemoveFormatting, LayoutTemplate, ChevronDown } from "lucide-react";

import "../parametresStyles/ParametrageAttestations.css";

import {
    fetchAttestationsRequest,
    createAttestationRequest,
    updateAttestationRequest,
    deleteAttestationRequest,
} from "../../../../redux/actions/attestationsActions";

const TEMPLATES = {
    travail: {
        label: "Attestation de Travail",
        html: `Nous soussignés, [nom_entreprise], dont le siège social est sis à : [adresse_entreprise], attestons par la présente que [civilite] [prenom] [nom], titulaire de la CNI N° [cin] et immatriculé(e) à la Caisse Nationale de la Sécurité Sociale sous le N° [cnss], est employé(e) par notre société depuis le [date_entree] en qualité de « [poste] ».

La présente attestation est délivrée à l'intéressé(e) pour servir et valoir ce que de droit.`,
    },
    salaire: {
        label: "Attestation de Salaire",
        html: `Nous soussignés, [nom_entreprise], dont le siège social est sis à : [adresse_entreprise], attestons par la présente que [civilite] [prenom] [nom], titulaire de la CNI N° [cin] et immatriculé(e) à la Caisse Nationale de la Sécurité Sociale sous le N° [cnss], est employé(e) par notre société depuis le [date_entree] en qualité de « [poste] ».

Il/Elle perçoit un salaire net mensuel de [salaire_net] Dirhams ([salaire_net_en_lettres] Dirhams).

La présente attestation est délivrée à l'intéressé(e) pour servir et valoir ce que de droit.`,
    },
};

const DYNAMIC_ATTRIBUTES = [
    { key: "[salaire_net]", label: "Salaire net (en chiffres)", category: "Professionnel" },
    { key: "[salaire_net_en_lettres]", label: "Salaire net (en lettres)", category: "Professionnel" },
    { key: "[nom]",           label: "Nom",           category: "Identité" },
    { key: "[prenom]",        label: "Prénom",         category: "Identité" },
    { key: "[civilite]",      label: "Civilité (M. / Mme)", category: "Identité" },
    { key: "[cin]",           label: "CIN",            category: "Identité" },
    { key: "[matricule]",     label: "Matricule",      category: "Identité" },
    { key: "[adresse]",       label: "Adresse",        category: "Identité" },
    { key: "[cnss]",          label: "CNSS",           category: "Professionnel" },
    { key: "[poste]",         label: "Poste",          category: "Professionnel" },
    { key: "[departement]",   label: "Département",    category: "Professionnel" },
    { key: "[date_entree]",   label: "Date d'entrée",  category: "Professionnel" },
    { key: "[date_actuelle]", label: "Date actuelle",  category: "Système" },
    { key: "[nom_entreprise]",label: "Nom entreprise", category: "Système" },
    { key: "[adresse_entreprise]",label: "Adresse entreprise", category: "Système" },
];

function groupByCategory(attrs) {
    return attrs.reduce((acc, a) => {
        if (!acc[a.category]) acc[a.category] = [];
        acc[a.category].push(a);
        return acc;
    }, {});
}

function htmlToPlainText(value) {
    return String(value)
        .replace(/<br\s*\/?\s*>/gi, "\n")
        .replace(/<\/p>|<\/h[1-6]>/gi, "\n")
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/gi, " ")
        .trim();
}

export default function ParametrageAttestations() {
    const dispatch = useDispatch();
    const { loading } = useSelector((s) => s.attestations || {});
    const list = useSelector((s) => s.attestations?.attestations || []);

    const [showAddModal, setShowAddModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [form, setForm] = useState({
        nom: "",
        description: "",
        lieuDate: "Agadir, le [date_actuelle]",
        titre: "",
        signatureLigne1: "Directeur des Ressources Humaines",
        signatureLigne2: "M. Mohamed EL GHAZLANI",
        downloadMode: "FREE",
        corps: "",
        actif: true,
    });

    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewItem, setPreviewItem] = useState(null);

    const bodyRef = useRef(null);
    const [showTemplateMenu, setShowTemplateMenu] = useState(false);
    const groupedAttributes = useMemo(() => groupByCategory(DYNAMIC_ATTRIBUTES), []);

    useEffect(() => {
        dispatch(fetchAttestationsRequest());
    }, [dispatch]);

    /* ── helpers ── */
    const resetForm = () => {
        setForm({ nom: "", description: "", lieuDate: "Agadir, le [date_actuelle]", titre: "", signatureLigne1: "Directeur des Ressources Humaines", signatureLigne2: "M. Mohamed EL GHAZLANI", downloadMode: "FREE", corps: "", actif: true });
    };

    const openCreate  = () => { setEditingItem(null); resetForm(); setShowAddModal(true); };
    const openEdit = (item) => {
        setEditingItem(item);
        setForm({ nom: item.nom || "", description: item.description || "", lieuDate: item.lieuDate || "Agadir, le [date_actuelle]", titre: item.titre || item.nom || "", signatureLigne1: item.signatureLigne1 || "Directeur des Ressources Humaines", signatureLigne2: item.signatureLigne2 || "M. Mohamed EL GHAZLANI", downloadMode: item.downloadMode || "FREE", corps: htmlToPlainText(item.corps || ""), actif: item.actif !== false });
        setShowAddModal(true);
    };
    const closeCreate = () => { setShowAddModal(false); setEditingItem(null); resetForm(); };

    const insertTextAtCursor = (text) => {
        const field = bodyRef.current;
        const start = field?.selectionStart ?? form.corps.length;
        const end = field?.selectionEnd ?? start;
        setForm((p) => ({ ...p, corps: `${p.corps.slice(0, start)}${text}${p.corps.slice(end)}` }));
        requestAnimationFrame(() => {
            field?.focus();
            field?.setSelectionRange(start + text.length, start + text.length);
        });
    };

    const applyTemplate = (key) => {
        const tpl = TEMPLATES[key];
        if (!tpl) return;
        const hasContent = form.corps && String(form.corps).replace(/<[^>]*>/g, "").trim();
        if (hasContent) {
            Swal.fire({
                title: "Remplacer le contenu ?",
                text: "Le contenu actuel sera remplacé par le template sélectionné.",
                icon: "question",
                showCancelButton: true,
                confirmButtonColor: "#204385",
                cancelButtonColor: "#6b7280",
                confirmButtonText: "Oui, appliquer",
                cancelButtonText: "Annuler",
            }).then((r) => {
                if (r.isConfirmed) {
                    setForm((p) => ({ ...p, corps: tpl.html }));
                }
            });
        } else {
            setForm((p) => ({ ...p, corps: tpl.html }));
        }
        setShowTemplateMenu(false);
    };

    /* ── submit ── */
    const submitCreate = () => {
        if (!form.nom.trim()) {
            Swal.fire({ icon: "warning", title: "Champ requis", text: "Le nom est obligatoire.", confirmButtonColor: "#204385" });
            return;
        }
        if (!form.corps || !String(form.corps).replace(/<[^>]*>/g, "").trim()) {
            Swal.fire({ icon: "warning", title: "Champ requis", text: "Le corps est obligatoire.", confirmButtonColor: "#204385" });
            return;
        }
        const payload = {
            nom: form.nom.trim(),
            description: form.description || "",
            lieuDate: form.lieuDate.trim(),
            titre: form.titre.trim() || form.nom.trim(),
            signatureLigne1: form.signatureLigne1.trim(),
            signatureLigne2: form.signatureLigne2.trim(),
            corps: form.corps,
            downloadMode: form.downloadMode,
            actif: form.actif,
        };
        dispatch(editingItem ? updateAttestationRequest({ id: editingItem.id, ...payload }) : createAttestationRequest(payload));
        closeCreate();
    };

    const confirmDelete = (id) => {
        Swal.fire({
            title: "Confirmation",
            text: "Voulez-vous vraiment supprimer cette attestation ?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#ee645f",
            cancelButtonColor: "#204385",
            confirmButtonText: "Oui, supprimer",
            cancelButtonText: "Annuler",
        }).then((r) => { if (r.isConfirmed) dispatch(deleteAttestationRequest(id)); });
    };

    const openPreview = (item) => { setPreviewItem(item); setPreviewOpen(true); };
    const closePreview = () => { setPreviewOpen(false); setPreviewItem(null); };

    /* ══════════════════════════════════════════
       MODAL — Création
    ══════════════════════════════════════════ */
    const renderModal = () => {
        if (!showAddModal) return null;
        return (
            <div className="pcModal" onMouseDown={closeCreate}>
                <div className="pcModal__panel" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                    <div className="pcModal__header">
                        <div className="pcModal__title">{editingItem ? "Modifier l'attestation" : "Nouvelle attestation"}</div>
                        <button className="pcIconBtn" onClick={closeCreate} aria-label="Fermer">
                            <X size={16} />
                        </button>
                    </div>

                    <div className="pcModal__body">
                        <div className="attForm">

                            {/* ligne 1 : nom + description */}
                            <div className="attRow2">
                                <div className="pcField">
                                    <label>Nom *</label>
                                    <input
                                        type="text"
                                        value={form.nom}
                                        onChange={(e) => setForm((p) => ({ ...p, nom: e.target.value }))}
                                        placeholder="Ex : Attestation de Travail"
                                    />
                                </div>
                                <div className="pcField">
                                    <label>Description</label>
                                    <input
                                        type="text"
                                        value={form.description}
                                        onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                                        placeholder="Optionnel"
                                    />
                                </div>
                            </div>

                            <div className="attRow2">
                                <div className="pcField">
                                    <label>Lieu et date</label>
                                    <input value={form.lieuDate} onChange={(e) => setForm((p) => ({ ...p, lieuDate: e.target.value }))} placeholder="Agadir, le [date_actuelle]" />
                                </div>
                                <div className="pcField">
                                    <label>Titre du PDF</label>
                                    <input value={form.titre} onChange={(e) => setForm((p) => ({ ...p, titre: e.target.value }))} placeholder="ATTESTATION DE TRAVAIL" />
                                </div>
                            </div>

                            {/* ligne 2 : mode */}
                            <div className="attRow2">
                                <div className="pcField">
                                    <label>Mode de téléchargement *</label>
                                    <select
                                        value={form.downloadMode}
                                        onChange={(e) => setForm((p) => ({ ...p, downloadMode: e.target.value }))}
                                    >
                                        <option value="FREE">LIBRE — téléchargement direct</option>
                                        <option value="MOTIF">DEMANDE — motif / validation</option>
                                    </select>
                                </div>
                            </div>

                            {/* éditeur — full width */}
                            <div className="pcField attFieldFull">
                                <label>Corps (éditeur) *</label>

                                <div className="attEditorToolbar" style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '10px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                                    <div className="attSimpleGuide">
                                        <strong>Corps de l'attestation</strong>
                                        <span>Écrivez uniquement le texte central. La date, le titre et la signature sont ajoutés automatiquement dans le PDF.</span>
                                    </div>
                                    <div className="attTemplateBlock" style={{ position: "relative" }}>
                                        <button 
                                            type="button" 
                                            className="attToolbarBtn" 
                                            style={{ width: "auto", padding: "6px 12px", color: "#204385", fontWeight: "bold", background: "#fff", border: "1px solid #d1d5db", borderRadius: 4 }}
                                            onClick={() => setShowTemplateMenu(!showTemplateMenu)}
                                        >
                                            <LayoutTemplate size={14} style={{ marginRight: 6 }} />
                                            Insérer un template
                                            <ChevronDown size={14} style={{ marginLeft: 6 }} />
                                        </button>
                                        
                                        {showTemplateMenu && (
                                            <div className="attTemplateMenu" style={{ position: "absolute", top: "100%", left: 0, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 4, boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)", zIndex: 10, minWidth: 200, padding: 4, marginTop: 4 }}>
                                                {Object.entries(TEMPLATES).map(([key, tpl]) => (
                                                    <button
                                                        key={key}
                                                        type="button"
                                                        style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 12px", border: "none", background: "none", cursor: "pointer", fontSize: 12, borderRadius: 2 }}
                                                        onMouseEnter={(e) => e.currentTarget.style.background = "#f3f4f6"}
                                                        onMouseLeave={(e) => e.currentTarget.style.background = "none"}
                                                        onClick={() => applyTemplate(key)}
                                                    >
                                                        {tpl.label}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* variables */}
                                    <div className="attVarsHead" style={{ fontWeight: 'bold', color: '#374151' }}>
                                        <Code size={13} style={{ marginRight: 4 }} /> Variables dynamiques
                                    </div>
                                    <div className="attVarsGrid" style={{ background: '#fff', padding: '10px', border: '1px solid #e5e7eb', borderRadius: 4, maxHeight: '150px', overflowY: 'auto' }}>
                                        {Object.keys(groupedAttributes).map((cat) => (
                                            <div key={cat} className="attVarsCol">
                                                <div className="attVarsCat">{cat}</div>
                                                {groupedAttributes[cat].map((v) => (
                                                    <button
                                                        key={v.key}
                                                        type="button"
                                                        className="attVarBtn"
                                                        onMouseDown={(e) => e.preventDefault()}
                                                        onClick={() => insertTextAtCursor(v.key)}
                                                        title={v.key}
                                                    >
                                                        <span>{v.label}</span>
                                                        <code>{v.key}</code>
                                                    </button>
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="attEditor attEditor--plain">
                                    <textarea
                                        ref={bodyRef}
                                        value={form.corps}
                                        onChange={(e) => setForm((p) => ({ ...p, corps: e.target.value }))}
                                        placeholder="Ex. Nous soussignés, [nom_entreprise], attestons que [civilite] [prenom] [nom]..."
                                    />
                                </div>
                                <div className="attHint">
                                    Cliquez sur une variable pour l'insérer au curseur (ex.&nbsp;<code>[nom]</code>).
                                </div>
                            </div>

                            <div className="attRow2">
                                <div className="pcField">
                                    <label>Signature - ligne 1</label>
                                    <input value={form.signatureLigne1} onChange={(e) => setForm((p) => ({ ...p, signatureLigne1: e.target.value }))} placeholder="Directeur des Ressources Humaines" />
                                </div>
                                <div className="pcField">
                                    <label>Signature - ligne 2</label>
                                    <input value={form.signatureLigne2} onChange={(e) => setForm((p) => ({ ...p, signatureLigne2: e.target.value }))} placeholder="M. Mohamed EL GHAZLANI" />
                                </div>
                            </div>

                            {/* checkbox actif */}
                            <label className="attCheck attFieldFull">
                                <input
                                    type="checkbox"
                                    checked={!!form.actif}
                                    onChange={(e) => setForm((p) => ({ ...p, actif: e.target.checked }))}
                                />
                                <div>
                                    <div className="attCheck__title">Actif</div>
                                    <div className="attCheck__sub">Disponible pour les utilisateurs</div>
                                </div>
                            </label>
                        </div>
                    </div>

                    <div className="pcModal__footer">
                        <button className="pcBtn pcBtn--ghost" type="button" onClick={closeCreate}>
                            Annuler
                        </button>
                        <button className="pcBtn pcBtn--primary" type="button" onClick={submitCreate}>
                            {editingItem ? "Enregistrer" : "Créer"}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    /* ══════════════════════════════════════════
       MODAL — Aperçu
    ══════════════════════════════════════════ */
    const renderPreviewModal = () => {
        if (!previewOpen || !previewItem) return null;
        return (
            <div className="pcModal" onMouseDown={closePreview}>
                <div className="pcModal__panel pcModal__panel--sm" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                    <div className="pcModal__header">
                        <div className="pcModal__title">
                            <Eye size={14} style={{ marginRight: 6, verticalAlign: "middle" }} />
                            Aperçu — {previewItem.nom}
                        </div>
                        <button className="pcIconBtn" onClick={closePreview} aria-label="Fermer">
                            <X size={16} />
                        </button>
                    </div>

                    <div className="pcModal__body">
                        <div className="attPreviewBox">
                            <div
                                className="attPreviewContent"
                                dangerouslySetInnerHTML={{ __html: previewItem.corps || "" }}
                            />
                        </div>
                    </div>

                    <div className="pcModal__footer">
                        <button className="pcBtn pcBtn--ghost" type="button" onClick={closePreview}>
                            Fermer
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    /* ══════════════════════════════════════════
       RENDER
    ══════════════════════════════════════════ */
    return (
        <div className="pcPage">
            {/* Header */}
            <div className="pcHeader">
                <div>
                    <h1>Gestion des attestations</h1>
                    <p>Configurez les types d'attestations et leurs paramètres.</p>
                </div>
            </div>

            {/* Contenu */}
            <div className="pcWrap">
                <div className="pcCard">
                    <div className="pcBody">
                        <div className="pcTopRow">
                            <h2 className="pcTitle">Liste des attestations</h2>
                            <button
                                className="pcBtn pcBtn--primary"
                                type="button"
                                onClick={openCreate}
                                disabled={loading}
                            >
                                <Plus size={14} /> Ajouter une attestation
                            </button>
                        </div>

                        <div className="pcTableWrap">
                            <table className="pcTable">
                                <thead>
                                <tr>
                                    <th>Nom</th>
                                    <th>Description</th>
                                    <th>Mode</th>
                                    <th>Statut</th>
                                    <th className="is-right">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={5} className="pcEmptyCell">Chargement…</td>
                                    </tr>
                                ) : list.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="pcEmptyCell">Aucune attestation configurée</td>
                                    </tr>
                                ) : (
                                    list.map((a) => (
                                        <tr key={a.id}>
                                            <td>
                                                    <span className="pcInline">
                                                        <FileText size={14} className="pcAccent" />
                                                        <span className="pcStrong">{a.nom}</span>
                                                    </span>
                                            </td>
                                            <td className="pcMuted">{a.description || "—"}</td>
                                            <td>
                                                <span className="pcBadge">{a.downloadMode}</span>
                                            </td>
                                            <td>
                                                <button
                                                    className={`pcPill ${a.actif ? "is-on" : "is-off"}`}
                                                    type="button"
                                                    disabled
                                                >
                                                    {a.actif ? "Actif" : "Inactif"}
                                                </button>
                                            </td>
                                            <td className="is-right">
                                                <div className="pcRowActions">
                                                    <button
                                                        className="pcIconBtn"
                                                        type="button"
                                                        title="Aperçu"
                                                        onClick={() => openPreview(a)}
                                                        disabled={loading}
                                                    >
                                                        <Eye size={15} />
                                                    </button>
                                                    <button
                                                        className="pcIconBtn"
                                                        type="button"
                                                        title="Modifier"
                                                        onClick={() => openEdit(a)}
                                                        disabled={loading}
                                                    >
                                                        <Pencil size={15} />
                                                    </button>
                                                    <button
                                                        className="pcIconBtn pcIconBtn--danger"
                                                        type="button"
                                                        title="Supprimer"
                                                        onClick={() => confirmDelete(a.id)}
                                                        disabled={loading}
                                                    >
                                                        <Trash2 size={15} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {renderModal()}
            {renderPreviewModal()}
        </div>
    );
}
